import type { ProjectChangeset, ProjectEntry } from '@erp/rpc';
import {
  ArrowLeftIcon,
  FileIcon,
  FolderIcon,
  FolderPlusIcon,
  HeadCircuitIcon,
  MagnifyingGlassIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { DateTime, T, useGT, Var } from 'gt-react';
import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ChatInput, ChatMessageList } from '@/components/common/chat';
import { AnnotationToolbar } from '@/components/common/chat/AnnotationToolbar';
import type { ChatInputAttachment } from '@/components/common/chat/ChatInput';
import type {
  ImageAttachmentValue,
  Message,
  ToolCall,
} from '@/context/agent-state.context';
import { useAnnotationCanvas } from '@/context/annotation-canvas.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentModule } from '@/hooks/useAgentModule';
import { captureScreen } from '@/utils/capture-screen';
import { formatFileSize } from '@/lib/pricelist-utils';
import { ProjectFilePreviewDialog } from './ProjectFilePreviewDialog';
import { ProjectUploadTray } from './ProjectUploadTray';
import type { ProjectUploadTask } from './useProjectUploadQueue';

export interface ProjectFolderCrumb {
  id: string;
  name: string;
}

interface ProjectToolbarProps {
  search: string;
  setSearch: (value: string) => void;
  canCreate: boolean;
  uploadInput: RefObject<HTMLInputElement | null>;
  onUpload: (files: FileList | null) => void;
  onCreateFolder: () => void;
  changeset: ProjectChangeset | null | undefined;
  onReview: () => void;
}

function ProjectToolbar(props: ProjectToolbarProps) {
  const gt = useGT();
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative min-w-60 flex-1">
        <MagnifyingGlassIcon className="absolute left-3 top-2.5 text-muted-foreground" />
        <Input
          className="pl-9"
          value={props.search}
          onChange={(event) => props.setSearch(event.target.value)}
          placeholder={gt('Search file names and paths', {
            context: 'project file search placeholder',
          })}
        />
      </div>
      {props.canCreate && (
        <>
          <input
            ref={props.uploadInput}
            type="file"
            multiple
            hidden
            onChange={(event) => props.onUpload(event.target.files)}
          />
          <Button
            variant="outline"
            onClick={() => props.uploadInput.current?.click()}
          >
            <UploadSimpleIcon />
            <T context="project upload files button">Upload files</T>
          </Button>
          <Button variant="outline" onClick={() => props.onCreateFolder()}>
            <FolderPlusIcon />
            <T context="project create folder button">New folder</T>
          </Button>
        </>
      )}
      {props.changeset && (
        <Button onClick={() => props.onReview()}>
          <T context="review assistant project changes button">
            Review changes
          </T>
          <Badge variant="secondary">{props.changeset.items.length}</Badge>
        </Button>
      )}
    </div>
  );
}

function FolderPanel(props: {
  stack: ProjectFolderCrumb[];
  openRoot: () => void;
  openCrumb: (index: number) => void;
  entries: ProjectEntry[];
  openFolder: (entry: ProjectEntry) => void;
}) {
  return (
    <>
      <div className="border-b p-3 text-sm font-medium">
        <T context="project folders panel heading">Folders</T>
      </div>
      <ScrollArea className="min-h-0 flex-1 p-2">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted"
          onClick={() => props.openRoot()}
        >
          <FolderIcon weight="fill" />
          <T context="project root folder label">Project files</T>
        </button>
        {props.stack.map((folder, index) => (
          <button
            type="button"
            key={folder.id}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted"
            style={{ paddingLeft: `${(index + 2) * 8}px` }}
            onClick={() => props.openCrumb(index)}
          >
            <FolderIcon />
            {folder.name}
          </button>
        ))}
        {props.entries
          .filter((entry) => entry.kind === 'folder')
          .map((folder) => (
            <button
              type="button"
              key={folder.id}
              className="flex w-full items-center gap-2 rounded px-4 py-2 text-left text-sm hover:bg-muted"
              onClick={() => props.openFolder(folder)}
            >
              <FolderIcon className="shrink-0" />
              <span className="truncate">{folder.name}</span>
            </button>
          ))}
      </ScrollArea>
    </>
  );
}

function FilePanel(props: {
  entries: ProjectEntry[];
  selected: ProjectEntry | null;
  selectedInputIds: Set<string>;
  search: string;
  stack: ProjectFolderCrumb[];
  goBack: () => void;
  select: (entry: ProjectEntry) => void;
  openFolder: (entry: ProjectEntry) => void;
  toggleInput: (entryId: string) => void;
  openPreview: (entry: ProjectEntry) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-12 items-center gap-2 border-b px-3 text-sm">
        <Button
          size="icon"
          variant="ghost"
          disabled={props.stack.length === 0}
          onClick={() => props.goBack()}
        >
          <ArrowLeftIcon />
        </Button>
        <span className="truncate">
          /{props.stack.map((folder) => folder.name).join('/')}
        </span>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead className="w-10">
                <span className="sr-only">
                  <T context="project file focus selection column">
                    Include for the assistant
                  </T>
                </span>
              </TableHead>
              <TableHead>
                <T context="project files table name column">Name</T>
              </TableHead>
              <TableHead className="w-36">
                <T context="project files table updated column">Updated</T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {props.entries.map((entry) => (
              <TableRow
                key={entry.id}
                tabIndex={0}
                data-state={
                  props.selected?.id === entry.id ? 'selected' : undefined
                }
                className="cursor-pointer"
                onClick={() => props.select(entry)}
                onDoubleClick={() =>
                  entry.kind === 'folder'
                    ? props.openFolder(entry)
                    : props.openPreview(entry)
                }
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  if (entry.kind === 'folder') props.openFolder(entry);
                  else props.openPreview(entry);
                }}
              >
                <TableCell>
                  <Checkbox
                    checked={props.selectedInputIds.has(entry.id)}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={() => props.toggleInput(entry.id)}
                    aria-label={gt('Include in the next assistant turn', {
                      context: 'project file selection checkbox label',
                    })}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex min-w-0 items-center gap-2">
                    {entry.kind === 'folder' ? (
                      <FolderIcon className="shrink-0" weight="fill" />
                    ) : (
                      <FileIcon className="shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          className="truncate text-left font-medium hover:underline"
                          title={entry.name}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (entry.kind === 'folder')
                              props.openFolder(entry);
                            else props.openPreview(entry);
                          }}
                        >
                          {entry.name}
                        </button>
                        {entry.stagedChange && (
                          <Badge variant="outline">
                            <T context="staged project file badge">Staged</T>
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {entry.kind === 'file' ? (
                          formatFileSize(entry.sizeBytes ?? 0)
                        ) : (
                          <T context="project folder type">Folder</T>
                        )}
                      </p>
                      {props.search.trim() && (
                        <p className="truncate text-xs text-muted-foreground">
                          {entry.path}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className="text-xs text-muted-foreground"
                  title={entry.updatedBy.name}
                >
                  <DateTime options={{ dateStyle: 'medium' }}>
                    {entry.updatedAt}
                  </DateTime>
                </TableCell>
              </TableRow>
            ))}
            {props.entries.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="h-32 text-center text-muted-foreground"
                >
                  <T context="empty project folder message">
                    No files here. Upload files or open another folder.
                  </T>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

function ProjectNamiPanel(props: {
  messages: Message[];
  streamingText: string;
  error: string | null;
  activeToolCalls: ToolCall[];
  selectedCount: number;
  message: string;
  busy: boolean;
  canExecute: boolean;
  uploadInput: RefObject<HTMLInputElement | null>;
  attachments: ChatInputAttachment[];
  uploadsBlocked: boolean;
  onAttachFiles: (files: FileList | null) => void;
  onRemoveAttachment: (entryId: string) => void;
  onRetryAttachment: (entryId: string) => void;
  setMessage: (value: string) => void;
  send: (screenshot?: ImageAttachmentValue) => void;
  cancel: () => void;
  close: () => void;
  changeset: ProjectChangeset | null | undefined;
  onReview: () => void;
}) {
  const gt = useGT();
  const [isCapturingScreen, setIsCapturingScreen] = useState(false);
  const { isActive: isAnnotating, setActive, clear } = useAnnotationCanvas();
  const toggleScreenshot = () => setActive(!isAnnotating);
  const close = () => {
    setActive(false);
    clear();
    props.close();
  };
  const send = () => {
    if (!isAnnotating) {
      props.send();
      return;
    }
    setIsCapturingScreen(true);
    setActive(false);
    void captureScreen()
      .then((captured) => {
        clear();
        props.send({
          imageData: captured.imageData,
          mimeType: captured.mimeType,
        });
      })
      .catch(() => props.send())
      .finally(() => setIsCapturingScreen(false));
  };
  return (
    <div className="absolute inset-0 z-10 flex h-full min-h-0 flex-col bg-background">
      <div className="flex flex-shrink-0 items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <HeadCircuitIcon
            size={18}
            weight="duotone"
            className="text-muted-foreground"
          />
          <span className="text-sm font-medium">
            <T context="project AI assistant panel header">AI assistant</T>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="secondary">
            {props.selectedCount > 0 ? (
              <T context="project Nami focused file count">
                <Var>{props.selectedCount}</Var> focused
              </T>
            ) : (
              <T context="project Nami all files available badge">All files</T>
            )}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            className="h-7 w-7 p-0"
            title={gt('Close', {
              context: 'tooltip for closing the project Nami side panel',
            })}
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {props.messages.length === 0 && !props.busy && (
        <div className="flex flex-col gap-3 p-5">
          <p className="font-medium">
            <T context="project Nami welcome">Work with this project</T>
          </p>
          <p className="text-sm text-muted-foreground">
            <T context="project Nami welcome description">
              Explore your files, compare ideas, and create something together.
            </T>
          </p>
          <Button
            variant="outline"
            className="justify-start"
            disabled={!props.canExecute}
            onClick={() =>
              props.setMessage(
                gt('Summarize the files in this project.', {
                  context: 'project Nami starter prompt',
                }),
              )
            }
          >
            <T context="project Nami summarize suggestion">
              Summarize the files
            </T>
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            disabled={!props.canExecute}
            onClick={() =>
              props.setMessage(
                gt('Compare the requirements across these files.', {
                  context: 'project Nami compare prompt',
                }),
              )
            }
          >
            <T context="project Nami compare suggestion">
              Compare requirements
            </T>
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            disabled={!props.canExecute}
            onClick={() =>
              props.setMessage(
                gt('Draft follow-up questions based on these files.', {
                  context: 'project Nami follow-up prompt',
                }),
              )
            }
          >
            <T context="project Nami follow-up suggestion">Draft a follow-up</T>
          </Button>
        </div>
      )}
      <ChatMessageList
        messages={props.messages}
        streamingText={props.streamingText}
        isLoading={props.busy}
        isCapturingScreen={isCapturingScreen}
        error={props.error}
        activeToolCalls={props.activeToolCalls}
        className="min-h-0 flex-1"
        messageMaxWidth="max-w-[92%]"
      />
      {props.changeset && (
        <div className="mx-3 mb-3 flex flex-col gap-2 rounded-lg border bg-muted/30 p-3">
          <p className="text-sm font-medium">
            <T context="Nami changes ready heading">Ready for your review</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T context="Nami pending file changes count">
              <Var>{props.changeset.items.length}</Var> file changes are staged.
            </T>
          </p>
          <Button variant="outline" onClick={props.onReview}>
            <T context="review Nami project changes button">Review changes</T>
          </Button>
        </div>
      )}
      <div className="flex-shrink-0 border-t p-3">
        {props.canExecute ? (
          <>
            <input
              ref={props.uploadInput}
              type="file"
              multiple
              hidden
              onChange={(event) => props.onAttachFiles(event.target.files)}
            />
            <ChatInput
              value={props.message}
              onChange={(value) => props.setMessage(value)}
              onSend={send}
              onCancel={() => props.cancel()}
              isLoading={props.busy || isCapturingScreen}
              placeholder={gt('Ask the assistant about this project…', {
                context: 'project Nami prompt placeholder',
              })}
              autoFocus
              enableVoiceInput
              onVoiceTranscription={(text) => props.setMessage(text ?? '')}
              attachments={props.attachments}
              sendDisabled={props.uploadsBlocked}
              onAttachFile={() => props.uploadInput.current?.click()}
              onRetryAttachment={(entryId) => props.onRetryAttachment(entryId)}
              onRemoveAttachment={(entryId) =>
                props.onRemoveAttachment(entryId)
              }
              onShareToggle={toggleScreenshot}
              isShareActive={isAnnotating}
              annotationToolbar={<AnnotationToolbar />}
            />
          </>
        ) : (
          <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            <T context="read-only project Nami message">
              You have read-only access to this project conversation.
            </T>
          </p>
        )}
      </div>
    </div>
  );
}

function ChangesetSheet(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  changeset: ProjectChangeset | null | undefined;
  canExecute: boolean;
  publish: () => void;
  discard: () => void;
}) {
  return (
    <Sheet open={props.open} onOpenChange={(open) => props.setOpen(open)}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            <T context="project changes review sheet title">
              Review the assistant's changes
            </T>
          </SheetTitle>
          <SheetDescription>
            <T context="project changes review sheet description">
              Publishing applies every staged file as one transaction. If any
              file conflicts, nothing is published.
            </T>
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="my-5 h-[65vh] px-1">
          <div className="space-y-3">
            {props.changeset?.items.map((item) => (
              <div key={item.id} className="rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <Badge>
                    {item.operation === 'create' ? (
                      <T context="Nami changeset create operation">Create</T>
                    ) : (
                      <T context="Nami changeset replace operation">Replace</T>
                    )}
                  </Badge>
                  <span className="truncate font-medium">{item.path}</span>
                </div>
                {item.summary && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.summary}
                  </p>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {props.canExecute && props.changeset && (
          <SheetFooter>
            <Button variant="outline" onClick={() => props.discard()}>
              <T context="discard all Nami project changes button">
                Discard all
              </T>
            </Button>
            <Button onClick={() => props.publish()}>
              <T context="publish all Nami project changes button">
                Publish all
              </T>
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

export interface ProjectWorkspaceViewProps {
  projectId: string;
  search: string;
  setSearch: (value: string) => void;
  canCreate: boolean;
  canExecute: boolean;
  uploadInput: RefObject<HTMLInputElement | null>;
  namiUploadInput: RefObject<HTMLInputElement | null>;
  onUpload: (files: FileList | null) => void;
  onCreateFolder: () => void;
  onNamiUpload: (files: FileList | null) => void;
  namiAttachments: ChatInputAttachment[];
  namiUploadsBlocked: boolean;
  removeNamiAttachment: (entryId: string) => void;
  retryNamiAttachment: (entryId: string) => void;
  selectedCount: number;
  entries: ProjectEntry[];
  selected: ProjectEntry | null;
  selectedInputIds: Set<string>;
  stack: ProjectFolderCrumb[];
  select: (entry: ProjectEntry) => void;
  openFolder: (entry: ProjectEntry) => void;
  openRoot: () => void;
  openCrumb: (index: number) => void;
  goBack: () => void;
  toggleInput: (entryId: string) => void;
  changeset: ProjectChangeset | null | undefined;
  reviewOpen: boolean;
  setReviewOpen: (open: boolean) => void;
  publish: () => void;
  discard: () => void;
  messages: Message[];
  streamingText: string;
  chatError: string | null;
  activeToolCalls: ToolCall[];
  message: string;
  chatBusy: boolean;
  setMessage: (value: string) => void;
  send: (screenshot?: ImageAttachmentValue) => void;
  cancel: () => void;
  uploadTasks: ProjectUploadTask[];
  cancelUpload: (taskId: string) => void;
  retryUpload: (taskId: string) => void;
  uploadLeavePromptOpen: boolean;
  confirmUploadLeave: () => void;
  cancelUploadLeave: () => void;
}

export function ProjectWorkspaceView(props: ProjectWorkspaceViewProps) {
  const [previewEntry, setPreviewEntry] = useState<ProjectEntry | null>(null);
  const { containerElement, moduleConfig } = useAgentContainer();
  const gt = useGT();
  const [showFolders, setShowFolders] = useState(false);
  const {
    isOpen: isAgentPanelOpen,
    open: openAgentPanel,
    close: closeAgentPanel,
  } = useAgentPanel();
  useEffect(() => {
    if (
      moduleConfig?.moduleKey === 'projects' &&
      moduleConfig.currentPage === `/projects/${props.projectId}`
    )
      openAgentPanel();
  }, [
    openAgentPanel,
    props.projectId,
    moduleConfig?.moduleKey,
    moduleConfig?.currentPage,
  ]);
  const askAboutFile = (entry: ProjectEntry) => {
    if (!props.selectedInputIds.has(entry.id)) props.toggleInput(entry.id);
    props.setMessage(
      gt('About "{path}": ', {
        path: entry.path,
        context: 'project file chat prompt prefix',
      }),
    );
    setPreviewEntry(null);
    openAgentPanel();
  };
  useAgentModule({
    moduleKey: 'projects',
    currentPage: `/projects/${props.projectId}`,
    forcePanelEnabled: true,
    customRenderer: true,
  });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          onClick={() => setShowFolders(!showFolders)}
          aria-expanded={showFolders}
        >
          <FolderIcon />
          <T context="toggle project folders">Folders</T>
        </Button>
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          <T context="project files workspace label">Project files</T>
        </span>
        {props.selectedCount > 0 && (
          <Button
            variant="outline"
            onClick={() => {
              props.setMessage(
                gt('Help me with the focused files.', {
                  context: 'ask about selected project files',
                }),
              );
              openAgentPanel();
            }}
          >
            <T context="ask the assistant about selected files">
              Ask the assistant · <Var>{props.selectedCount}</Var> focused
            </T>
          </Button>
        )}
        <Button variant="outline" onClick={() => openAgentPanel()}>
          <HeadCircuitIcon />
          <T context="open project chat">Ask the assistant</T>
        </Button>
      </div>
      <ProjectToolbar {...props} onReview={() => props.setReviewOpen(true)} />
      <div
        className="relative min-h-[36rem]"
        style={{ height: 'calc(100dvh - 13rem)' }}
      >
        <ResizablePanelGroup
          direction="horizontal"
          className="overflow-hidden rounded-lg border bg-background"
        >
          {showFolders && (
            <>
              <ResizablePanel
                defaultSize={24}
                minSize={18}
                className="overflow-hidden"
              >
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  <FolderPanel
                    entries={props.entries}
                    openFolder={props.openFolder}
                    stack={props.stack}
                    openRoot={() => props.openRoot()}
                    openCrumb={(index) => props.openCrumb(index)}
                  />
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
            </>
          )}
          <ResizablePanel
            defaultSize={76}
            minSize={50}
            className="overflow-hidden"
          >
            <FilePanel
              {...props}
              openPreview={(entry) => {
                props.select(entry);
                setPreviewEntry(entry);
              }}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
        <ProjectUploadTray
          tasks={props.uploadTasks}
          cancel={props.cancelUpload}
          retry={props.retryUpload}
        />
      </div>
      <ProjectFilePreviewDialog
        projectId={props.projectId}
        entry={previewEntry}
        files={props.entries.filter((entry) => entry.kind === 'file')}
        onEntryChange={(entry) => {
          props.select(entry);
          setPreviewEntry(entry);
        }}
        onClose={() => setPreviewEntry(null)}
        onAskNami={props.canExecute ? askAboutFile : undefined}
      />
      <ChangesetSheet
        {...props}
        open={props.reviewOpen}
        setOpen={(open) => props.setReviewOpen(open)}
      />
      {isAgentPanelOpen &&
        containerElement &&
        createPortal(
          <ProjectNamiPanel
            messages={props.messages}
            streamingText={props.streamingText}
            error={props.chatError}
            activeToolCalls={props.activeToolCalls}
            selectedCount={props.selectedCount}
            message={props.message}
            busy={props.chatBusy}
            canExecute={props.canExecute}
            uploadInput={props.namiUploadInput}
            attachments={props.namiAttachments}
            uploadsBlocked={props.namiUploadsBlocked}
            onAttachFiles={(files) => props.onNamiUpload(files)}
            onRetryAttachment={(entryId) => props.retryNamiAttachment(entryId)}
            onRemoveAttachment={(entryId) =>
              props.removeNamiAttachment(entryId)
            }
            setMessage={(value) => props.setMessage(value)}
            send={(screenshot) => props.send(screenshot)}
            cancel={() => props.cancel()}
            close={closeAgentPanel}
            changeset={props.changeset}
            onReview={() => props.setReviewOpen(true)}
          />,
          containerElement,
        )}
      <AlertDialog
        open={props.uploadLeavePromptOpen}
        onOpenChange={(open) => !open && props.cancelUploadLeave()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="project active uploads leave confirmation title">
                Cancel active uploads?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="project active uploads leave confirmation description">
                Leaving this project will cancel queued and active file uploads.
                Completed files will stay in the project.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => props.cancelUploadLeave()}>
              <T context="stay on project during active uploads button">
                Stay here
              </T>
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => props.confirmUploadLeave()}>
              <T context="leave project and cancel active uploads button">
                Cancel uploads and leave
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
